import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trashService } from '../../services/trash.service';
import { FONT_SANS, FONT_SERIF } from '../landing/landingData';
import {
    Dots, FillButton, FilterDropdown, ICON, Icon, OutlineButton, PageHead, PlusIcon, ProjectCard, ProjectMenu, SearchBox, STATUS_DOT, STATUS_WORD,
    ViewToggle, fmtAgo, useDash, type DashProject, type Layout,
} from './dashUi';

type TypeF = 'all' | 'site' | 'video';
type StatusF = 'all' | 'live' | 'draft';

const TYPE_OPTS: Array<[TypeF, string]> = [['all', 'All'], ['site', 'Websites'], ['video', 'Videos']];
const STATUS_OPTS: Array<[StatusF, string]> = [['all', 'All'], ['live', 'Live & ready'], ['draft', 'Drafts']];
const LAYOUT_KEY = 'hyvay:dash-projects-layout';
const PAGE = 8;
const COLS = 'minmax(200px,2.4fr) minmax(110px,1fr) minmax(110px,1fr) minmax(130px,1fr) 40px';

const readLayout = (): Layout => {
    try { return localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'grid'; } catch { return 'grid'; }
};
const typeFromUrl = (v: string | null): TypeF => (v === 'video' || v === 'site' ? v : 'all');
const matchesStatus = (p: DashProject, f: StatusF) =>
    f === 'all' || (f === 'live' ? p.status === 'live' || p.status === 'ready' : p.status === 'draft' || p.status === 'building');

/** Projects: every website and video, filterable, as a card grid or a paged list. */
export const DashProjects: React.FC = () => {
    const dash = useDash();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const typeF = typeFromUrl(params.get('type'));
    const [statusF, setStatusF] = useState<StatusF>('all');
    const [query, setQuery] = useState('');
    const [layout, setLayoutState] = useState<Layout>(readLayout);
    const [sortAsc, setSortAsc] = useState(false);
    const [page, setPage] = useState(0);
    const [menuFor, setMenuFor] = useState<string | null>(null);
    const [trashCount, setTrashCount] = useState(0);

    useEffect(() => {
        let alive = true;
        trashService.list().then((t) => { if (alive) setTrashCount(t.items.length); }).catch(() => { /* badge is optional */ });
        return () => { alive = false; };
    }, [dash.projects]);

    const setType = (v: TypeF) => {
        const next = new URLSearchParams(params);
        if (v === 'all') next.delete('type'); else next.set('type', v);
        setParams(next, { replace: true });
        setPage(0);
    };
    const setLayout = (v: Layout) => {
        setLayoutState(v);
        try { localStorage.setItem(LAYOUT_KEY, v); } catch { /* private mode */ }
    };
    const clearFilters = () => { setType('all'); setStatusF('all'); setQuery(''); };

    const goCreate = () => {
        navigate('/dashboard');
        // Home mounts on the next frames; focus its composer once it is there.
        let tries = 0;
        const tick = () => {
            const el = document.querySelector<HTMLTextAreaElement>('.hv-composer textarea');
            if (el) { el.focus(); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
            if (++tries < 30) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    };

    const q = query.trim().toLowerCase();
    const filtered = useMemo(
        () => dash.projects.filter((p) => (typeF === 'all' || p.kind === typeF) && matchesStatus(p, statusF) && (!q || p.name.toLowerCase().includes(q))),
        [dash.projects, typeF, statusF, q],
    );
    const sorted = useMemo(
        () => filtered.slice().sort((a, b) => (sortAsc ? 1 : -1) * (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())),
        [filtered, sortAsc],
    );
    const pages = Math.max(1, Math.ceil(sorted.length / PAGE));
    const pg = Math.min(page, pages - 1);
    const paged = sorted.slice(pg * PAGE, (pg + 1) * PAGE);
    const from = sorted.length ? pg * PAGE + 1 : 0;
    const to = Math.min(sorted.length, (pg + 1) * PAGE);

    const noProjects = dash.projects.length === 0;
    const filtersOn = typeF !== 'all' || statusF !== 'all' || !!q;

    return (
        <section data-screen-label="Projects" style={{ display: 'flex', flexDirection: 'column', gap: 18, animation: 'hvRise 0.4s cubic-bezier(0.22,0.9,0.24,1) both' }}>
            <style>{'.hv-dash .hv-pj-row:hover{background:var(--hv-card-2)}.hv-dash .hv-pj-sort:hover,.hv-dash .hv-pj-pg:hover:not(:disabled){color:var(--hv-ink)}.hv-dash .hv-pj-pg:hover:not(:disabled){background:var(--hv-page-2)}'}</style>
            <PageHead title="Projects" lead="Every website and video you've made, in one place.">
                <OutlineButton onClick={() => navigate('/dashboard/trash')} aria-label={trashCount > 0 ? `Trash, ${trashCount} item${trashCount === 1 ? '' : 's'}` : 'Trash'} style={{ height: 36 }}>
                    <Icon d={ICON.trash} size={15} />Trash
                    {trashCount > 0 && <span style={{ minWidth: 18, height: 18, padding: '0 6px', borderRadius: 999, background: 'var(--hv-page-2)', color: 'var(--hv-body)', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>{trashCount}</span>}
                </OutlineButton>
                <FillButton icon={<PlusIcon />} onClick={goCreate}>New project</FillButton>
            </PageHead>

            <div style={{ marginTop: 14, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <FilterDropdown label="Type" value={typeF} options={TYPE_OPTS} onChange={setType} />
                <FilterDropdown label="Status" value={statusF} options={STATUS_OPTS} onChange={(v) => { setStatusF(v); setPage(0); }} />
                <div style={{ flex: 1 }} />
                <ViewToggle value={layout} onChange={setLayout} />
                <SearchBox value={query} onChange={(v) => { setQuery(v); setPage(0); }} placeholder="Search projects" />
            </div>

            {dash.projectsLoading && noProjects ? (
                <div style={{ padding: '24px 0', fontFamily: FONT_SANS, fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>LOADING…</div>
            ) : filtered.length === 0 ? (
                <div style={{ padding: '64px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ fontFamily: FONT_SERIF, fontSize: 26, letterSpacing: '0.01em', wordSpacing: '0.06em' }}>{noProjects ? 'No projects yet' : 'Nothing matches'}</div>
                    <div style={{ marginTop: 6, fontSize: 13.5, color: 'var(--hv-body)' }}>
                        {noProjects ? 'Create your first site or video from Home.' : q ? `No project matches “${query.trim()}”.` : 'Try a different filter.'}
                    </div>
                    <div style={{ marginTop: 18 }}>
                        {noProjects
                            ? <FillButton icon={<PlusIcon />} onClick={goCreate}>New project</FillButton>
                            : filtersOn && <OutlineButton onClick={clearFilters}>Clear filters</OutlineButton>}
                    </div>
                </div>
            ) : layout === 'grid' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 16 }}>
                    {sorted.map((p) => <ProjectCard key={`${p.kind}-${p.id}`} p={p} />)}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="hv-db-row" style={{ display: 'grid', gap: 24, alignItems: 'center', padding: '0 20px', height: 40, background: 'var(--hv-page-2)', borderTop: '1px solid var(--hv-line)', borderBottom: '1px solid var(--hv-line)', fontSize: 12, fontWeight: 600, color: 'var(--hv-body)', gridTemplateColumns: COLS }}>
                        <span>Name</span>
                        <span className="hv-db-hide-sm">Type</span>
                        <span>Status</span>
                        <button type="button" className="hv-db-hide-sm hv-pj-sort" onClick={() => { setSortAsc((s) => !s); setPage(0); }} aria-label={`Sort by last updated, ${sortAsc ? 'oldest' : 'newest'} first`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 0, border: 0, background: 'transparent', font: 'inherit', color: 'inherit', cursor: 'pointer', justifySelf: 'start' }}>
                            Last updated
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: `rotate(${sortAsc ? 180 : 0}deg)`, transition: 'transform 0.2s ease' }} aria-hidden="true"><path d="M12 5v14M6 13l6 6 6-6" /></svg>
                        </button>
                        <span />
                    </div>
                    {paged.map((p) => {
                        const key = `${p.kind}-${p.id}`;
                        return (
                            <div key={key} className="hv-db-row hv-pj-row" role="button" tabIndex={0} onClick={() => dash.openProject(p)} onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) dash.openProject(p); }} style={{ position: 'relative', display: 'grid', gap: 24, alignItems: 'center', padding: '0 20px', minHeight: 48, borderBottom: '1px solid var(--hv-line)', cursor: 'pointer', fontSize: 13, gridTemplateColumns: COLS, zIndex: menuFor === key ? 2 : undefined }}>
                                <span className="hv-rowname" style={{ justifySelf: 'start', maxWidth: '100%', minWidth: 0, padding: '4px 0', margin: '-4px 0', color: 'var(--hv-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textUnderlineOffset: 3, textDecorationThickness: 1 }}>{p.name}</span>
                                <span className="hv-db-hide-sm" style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--hv-ink)' }}>
                                    <Icon d={p.kind === 'site' ? ICON.site : ICON.video} size={14} sw={2} style={{ flex: 'none' }} />
                                    {p.kind === 'site' ? 'Website' : 'Video'}
                                </span>
                                <span>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 24, padding: '0 9px', borderRadius: 999, background: p.status === 'draft' ? 'var(--hv-sand)' : 'var(--hv-mint)', color: 'var(--hv-ink)', fontSize: 12, fontWeight: 600 }}>
                                        <span style={{ flex: 'none', width: 6, height: 6, borderRadius: 999, background: STATUS_DOT[p.status] }} />
                                        {STATUS_WORD[p.status]}
                                    </span>
                                </span>
                                <span className="hv-db-hide-sm" style={{ color: 'var(--hv-ink)', whiteSpace: 'nowrap' }}>{fmtAgo(p.updatedAt)}</span>
                                <button type="button" data-dismiss-ignore aria-label="Project actions" onClick={(e) => { e.stopPropagation(); setMenuFor((m) => (m === key ? null : key)); }} className="hv-iconbtn" style={{ width: 30, height: 30, borderRadius: 8, border: 0, background: 'transparent', color: 'var(--hv-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                    <Dots />
                                </button>
                                {menuFor === key && <ProjectMenu p={p} onClose={() => setMenuFor(null)} style={{ right: 14, top: 'calc(100% - 6px)' }} />}
                            </div>
                        );
                    })}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '14px 4px 0' }}>
                        <span style={{ fontSize: 12.5, color: 'var(--hv-muted)' }}>{`Showing ${from}–${to} of ${sorted.length}`}</span>
                        <div style={{ flex: 1 }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <PagerBtn label="Previous page" disabled={pg === 0} onClick={() => setPage(pg - 1)} d="M15 6l-6 6 6 6" />
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 6px', fontSize: 12.5, color: 'var(--hv-muted)' }}>
                                Page
                                <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <select value={pg} onChange={(e) => setPage(parseInt(e.target.value, 10) || 0)} aria-label="Go to page" style={{ appearance: 'none', WebkitAppearance: 'none', height: 32, padding: '0 28px 0 12px', borderRadius: 6, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', color: 'var(--hv-ink)', fontSize: 12.5, fontWeight: 400, cursor: 'pointer', outline: 'none' }}>
                                        {Array.from({ length: pages }, (_, i) => <option key={i} value={i}>{i + 1}</option>)}
                                    </select>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', right: 9, pointerEvents: 'none', color: 'var(--hv-muted)' }} aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                                </span>
                                of {pages}
                            </label>
                            <PagerBtn label="Next page" disabled={pg >= pages - 1} onClick={() => setPage(pg + 1)} d="M9 6l6 6-6 6" />
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

const PagerBtn: React.FC<{ label: string; disabled: boolean; onClick: () => void; d: string }> = ({ label, disabled, onClick, d }) => (
    <button type="button" className="hv-pj-pg" aria-label={label} disabled={disabled} onClick={onClick} style={{ width: 32, height: 32, borderRadius: 8, border: 0, background: 'transparent', color: 'var(--hv-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
    </button>
);

export default DashProjects;
